import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MobileTabBar } from '../components/MobileTabBar';
import {
  MousePointer, Film, Layers, LayoutGrid, Printer, Mail, ArrowLeft
} from 'lucide-react';

// Parent content categories — luxury minimal: monochrome black tiles with brass icon-overlay accent.
// One unified palette so it matches RealProp's existing desktop tone (white surface, black accents).
const CATEGORIES = [
  {
    key: 'post',
    label: 'Post',
    icon: MousePointer,
    description: 'Single-image posts',
    path: '/templates?type=post',
    testId: 'mobile-create-post',
  },
  {
    key: 'reel',
    label: 'Reel',
    icon: Film,
    description: 'Vertical video',
    path: '/reel-editor',
    testId: 'mobile-create-reel',
  },
  {
    key: 'story',
    label: 'Story',
    icon: Layers,
    description: '9:16 ephemeral',
    path: '/templates?type=story',
    testId: 'mobile-create-story',
  },
  {
    key: 'carousel',
    label: 'Carousel',
    icon: LayoutGrid,
    description: 'Multi-slide',
    path: '/carousel-editor',
    testId: 'mobile-create-carousel',
  },
  {
    key: 'print',
    label: 'Print',
    icon: Printer,
    description: 'Flyers, postcards',
    path: '/print-center',
    testId: 'mobile-create-print',
  },
  {
    key: 'newsletter',
    label: 'Newsletter',
    icon: Mail,
    description: 'Email blasts',
    path: '/templates?type=newsletter',
    testId: 'mobile-create-newsletter',
  },
];

const CategoryTile = ({ category, onClick }) => {
  const Icon = category.icon;
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-3 group active:scale-95 transition-transform"
      data-testid={category.testId}
    >
      <span
        className="flex items-center justify-center h-[88px] w-[88px] rounded-full bg-[#0B0C10] text-white shadow-md ring-1 ring-black/5 group-hover:shadow-lg transition-shadow"
        aria-hidden
      >
        <Icon className="h-9 w-9" strokeWidth={1.5} />
      </span>
      <span className="flex flex-col items-center">
        <span
          className="text-sm font-semibold text-black tracking-tight"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          {category.label}
        </span>
        <span className="text-[11px] text-gray-400 mt-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {category.description}
        </span>
      </span>
    </button>
  );
};

export const MobileCreatePage = () => {
  const navigate = useNavigate();
  useAuth(); // ensure auth context loaded (route protected at App level)

  return (
    <div
      className="min-h-screen bg-white text-black md:hidden"
      style={{ fontFamily: 'Manrope, sans-serif' }}
      data-testid="mobile-create-page"
    >
      {/* Header */}
      <header className="h-14 px-4 flex items-center justify-between border-b border-gray-100 sticky top-0 bg-white/95 backdrop-blur-xl z-30">
        <button
          onClick={() => navigate('/dashboard')}
          className="h-9 w-9 -ml-1 flex items-center justify-center rounded-full hover:bg-gray-100 active:scale-95 transition-all"
          data-testid="mobile-create-back"
          aria-label="Back to dashboard"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <span
          className="text-sm font-semibold tracking-tight"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Create New
        </span>
        <span className="w-9" />
      </header>

      {/* Hero copy */}
      <section className="px-6 pt-8 pb-6">
        <h1
          className="text-3xl font-light tracking-tight leading-[1.05]"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          What are we <span style={{ fontFamily: 'Playfair Display, serif', fontStyle: 'italic' }}>making</span>?
        </h1>
        <p className="text-sm text-gray-400 mt-2">
          Pick a format. We auto-fill your listing photos and brand.
        </p>
      </section>

      {/* 2 × 3 grid of round category tiles */}
      <section className="px-6 pb-32">
        <div className="grid grid-cols-2 gap-x-8 gap-y-10 mt-2">
          {CATEGORIES.map((cat) => (
            <CategoryTile
              key={cat.key}
              category={cat}
              onClick={() => navigate(cat.path)}
            />
          ))}
        </div>
      </section>

      <MobileTabBar />
    </div>
  );
};

// Desktop fallback: redirect to dashboard (mobile-only route)
export default function MobileCreatePageGuard() {
  const navigate = useNavigate();
  React.useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth >= 768) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);
  return <MobileCreatePage />;
}
